/**
 * One notification row, shared between the bell popover (compact) and the inbox
 * page (roomy). A single component so the icon language, unread treatment, and
 * click-to-open behaviour never drift between the two surfaces.
 *
 * Inbox layout is a strict three-column grid so rows align down the page
 * (DESIGN.md §14): a fixed-width icon circle, a flexible title+body column, and
 * a right-aligned time + hover-action column. The action buttons stay in flow
 * (opacity toggled, not mounted/unmounted) so hovering never shifts the row.
 *
 * Unread treatment (single-accent rule, §1): a thin brand-purple left rail
 * flush to the row's edge + stronger title text. Read rows lose the rail and
 * soften.
 */
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NotificationTypeIcon, RelativeTime } from './notification-meta';
import type { NotificationItem } from './notifications-procedures';

/**
 * BLU-1309 (F24): the compact popover normally shows title + time only. A
 * bridge-job starter's bells carry the useful part in the body (the SKU, and
 * what to do on a failure), so the popover shows it for exactly those: every
 * `print_file_resolved` bell, and the `system` failure bell that links to the
 * Print Files tab. Every other notification renders as before.
 */
const PRINT_FILES_ISSUES_LINK = '/production/issues?tab=print-files';
function showsBodyInPopover(item: NotificationItem): boolean {
  return item.type === 'print_file_resolved'
    || (item.type === 'system' && item.link === PRINT_FILES_ISSUES_LINK);
}

interface NotificationRowProps {
  item: NotificationItem;
  variant: 'popover' | 'inbox';
  /** Open the notification (marks read + navigates when it has a link). */
  onSelect: (item: NotificationItem) => void;
  /** Inbox only — dismiss (remove) the row. */
  onDismiss?: (item: NotificationItem) => void;
  /** Inbox only — mark a single unread row read without navigating. */
  onMarkRead?: (item: NotificationItem) => void;
  markReadLabel?: string;
  dismissLabel?: string;
}

export function NotificationRow({
  item,
  variant,
  onSelect,
  onDismiss,
  onMarkRead,
  markReadLabel = 'Mark as read',
  dismissLabel = 'Dismiss',
}: NotificationRowProps) {
  const unread = !item.read_at;
  const isInbox = variant === 'inbox';
  const showActions = isInbox && (onDismiss || (onMarkRead && unread));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={item.title}
      onClick={() => onSelect(item)}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative flex cursor-pointer items-start text-left',
        'transition-colors duration-150 motion-reduce:transition-none',
        'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isInbox
          ? 'gap-3 px-4 py-3 focus-visible:ring-inset'
          : 'gap-2.5 rounded-md px-2 py-2',
      )}
    >
      {/* Unread rail — the single brand accent, reserved for this signal.
          Flush to the row's left edge on the inbox; a soft pill in the popover. */}
      {unread && (
        <span
          aria-hidden="true"
          className={cn(
            'absolute left-0 bg-threadheads-purple',
            isInbox ? 'inset-y-0 w-[3px]' : 'inset-y-1.5 w-[3px] rounded-full',
          )}
        />
      )}

      <NotificationTypeIcon type={item.type} size={isInbox ? 'md' : 'sm'} />

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'line-clamp-2 text-sm',
            unread ? 'font-medium text-foreground' : 'font-normal text-foreground/80',
          )}
        >
          {item.title}
        </p>

        {item.body && (isInbox || showsBodyInPopover(item)) ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.body}</p>
        ) : null}

        {/* Popover keeps time under the title (compact). Inbox moves it to the
            right column so times align down the page. */}
        {!isInbox && (
          <RelativeTime
            iso={item.created_at}
            className={cn(
              'mt-1 block text-[11px] tabular-nums',
              unread ? 'text-muted-foreground' : 'text-muted-foreground/70',
            )}
          />
        )}
      </div>

      {/* Inbox right column: hover actions + right-aligned time, vertically
          centred against the row. Actions stay in flow (opacity), so the time
          never jumps when they reveal. */}
      {isInbox && (
        <div className="flex shrink-0 items-center gap-1 self-center pl-2">
          {showActions && (
            <div
              className={cn(
                'flex items-center gap-0.5',
                'opacity-0 transition-opacity duration-150 motion-reduce:transition-none',
                'group-hover:opacity-100 group-focus-within:opacity-100',
                '[@media(hover:none)]:opacity-100',
              )}
            >
              {onMarkRead && unread && (
                <button
                  type="button"
                  aria-label={markReadLabel}
                  title={markReadLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkRead(item);
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Check className="h-4 w-4" />
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  aria-label={dismissLabel}
                  title={dismissLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(item);
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <RelativeTime
            iso={item.created_at}
            className={cn(
              'whitespace-nowrap text-right text-[11px] tabular-nums',
              unread ? 'text-muted-foreground' : 'text-muted-foreground/70',
            )}
          />
        </div>
      )}

      {/* Unread dot for the compact popover (no room for hover actions). */}
      {!isInbox && unread && (
        <span
          aria-hidden="true"
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-threadheads-purple"
        />
      )}
    </div>
  );
}
